export const AUTOFILL_ANIMATION_NAME = "onAutoFillStart";

const IDENTITY_AUTOCOMPLETE = {
  billingEmail: "email",
  billingEmailConfirm: "email",
  billingFirstName: "given-name",
  billingLastName: "family-name",
  billingPhoneNumber: "tel",
  shippingFirstName: "given-name",
  shippingLastName: "family-name",
  shippingPhoneNumber: "tel",
};

const ADDRESS_AUTOCOMPLETE = {
  billingStreet3: "address-line1",
  shippingStreet3: "address-line1",
  billingStreet1: "address-line2",
  shippingStreet1: "address-line2",
  billingZipCode: "postal-code",
  shippingZipCode: "postal-code",
  billingCity: "address-level2",
  shippingCity: "address-level2",
  billingCountry: "country-name",
  shippingCountry: "country-name",
};

const ADDRESS_SIGNAL_FIELDS = [
  "billingStreet3",
  "billingZipCode",
  "billingCity",
  "shippingStreet3",
  "shippingZipCode",
  "shippingCity",
];

export const ADDRESS_DOM_FIELDS = [
  "billingStreet3",
  "billingStreet1",
  "billingZipCode",
  "billingCity",
  "billingCountry",
  "shippingStreet3",
  "shippingStreet1",
  "shippingZipCode",
  "shippingCity",
  "shippingCountry",
];

const IDENTITY_DOM_FIELDS = [
  "billingEmail",
  "billingEmailConfirm",
  "billingFirstName",
  "billingLastName",
  "billingPhoneNumber",
  "shippingFirstName",
  "shippingLastName",
  "shippingPhoneNumber",
];

export const PREFILL_DOM_FIELDS = [
  ...IDENTITY_DOM_FIELDS,
  ...ADDRESS_DOM_FIELDS,
];

const REQUIRED_BILLING_ADDRESS_FIELDS = [
  "billingStreet3",
  "billingZipCode",
  "billingCity",
];

const REQUIRED_SHIPPING_ADDRESS_FIELDS = [
  "shippingStreet3",
  "shippingZipCode",
  "shippingCity",
];

const HIDDEN_REQUIRED_BILLING_ADDRESS_FIELDS = [
  "billingZipCode",
  "billingCity",
];

const HIDDEN_REQUIRED_SHIPPING_ADDRESS_FIELDS = [
  "shippingZipCode",
  "shippingCity",
];

export const MISSING_ADDRESS_REVEAL_DELAY_MS = 700;

function isFilled(value) {
  if (typeof value === "string") {
    return value.trim().length > 0;
  }
  return Boolean(value);
}

export function getAutocompleteValue(fieldName) {
  return (
    IDENTITY_AUTOCOMPLETE[fieldName] ||
    ADDRESS_AUTOCOMPLETE[fieldName] ||
    "do-not-autofill"
  );
}

export function hasAddressPrefill(data = {}) {
  return ADDRESS_SIGNAL_FIELDS.some((field) => isFilled(data[field]));
}

export function isBrowserAutofillEvent(event) {
  if (!event) {
    return false;
  }
  if (event.type === "animationstart") {
    return event.animationName === AUTOFILL_ANIMATION_NAME;
  }
  return event.type === "input" || event.type === "change";
}

export function getMissingRequiredAddressFields(
  order = {},
  { sameAsBilling = true } = {},
) {
  const fields = [...REQUIRED_BILLING_ADDRESS_FIELDS];
  if (!sameAsBilling) {
    fields.push(...REQUIRED_SHIPPING_ADDRESS_FIELDS);
  }
  return fields.filter((name) => !isFilled(order[name]));
}

export function getMissingHiddenRequiredAddressFields(
  order = {},
  { sameAsBilling = true } = {},
) {
  const fields = [...HIDDEN_REQUIRED_BILLING_ADDRESS_FIELDS];
  if (!sameAsBilling) {
    fields.push(...HIDDEN_REQUIRED_SHIPPING_ADDRESS_FIELDS);
  }
  return fields.filter((name) => !isFilled(order[name]));
}

export function shouldScheduleMissingAddressReveal(
  order = {},
  values = {},
  { sameAsBilling = true } = {},
) {
  if (!hasAddressPrefill(order) && !hasAddressPrefill(values)) {
    return false;
  }
  return (
    getMissingHiddenRequiredAddressFields(order, { sameAsBilling }).length > 0
  );
}

export function createDeferredCallback(
  fn,
  delayMs = MISSING_ADDRESS_REVEAL_DELAY_MS,
) {
  let timeoutId = null;
  return {
    schedule() {
      if (timeoutId != null) {
        window.clearTimeout(timeoutId);
      }
      timeoutId = window.setTimeout(() => {
        timeoutId = null;
        fn();
      }, delayMs);
    },
    cancel() {
      if (timeoutId != null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
    },
  };
}

export function readAddressFieldsFromDom(root = document) {
  const values = {};
  for (const name of PREFILL_DOM_FIELDS) {
    const el =
      root.querySelector(`#${name}`) || root.querySelector(`[name="${name}"]`);
    if (!el || typeof el.value !== "string") {
      continue;
    }
    const value = el.value.trim();
    if (value) {
      values[name] = value;
    }
  }
  return values;
}

export function applyDomAddressToOrder(order = {}, values = {}) {
  let applied = false;
  for (const name of PREFILL_DOM_FIELDS) {
    const value = values[name];
    if (!isFilled(value) || order[name] === value) {
      continue;
    }
    order[name] = value;
    applied = true;
  }
  return applied;
}

export function startBrowserAutofillWatch(
  onTick,
  { intervalMs = 250, durationMs = 20000 } = {},
) {
  if (typeof onTick !== "function") {
    return () => {};
  }
  onTick();
  const intervalId = window.setInterval(onTick, intervalMs);
  const timeoutId = window.setTimeout(() => {
    window.clearInterval(intervalId);
  }, durationMs);
  return () => {
    window.clearInterval(intervalId);
    window.clearTimeout(timeoutId);
  };
}

export function revealMissingAddressFieldGroups(fieldNames = []) {
  for (const name of fieldNames) {
    document
      .querySelector(`.contact-form .${name}`)
      ?.classList.remove("d-none");
  }
}
